/**
 * DealSummaryTab - Aba de resumo do negócio
 * Layout compacto com métricas, descrição rica e anexos
 * 
 * Versão: 2.2.0
 * - Removidas informações duplicadas (já existem na sidebar)
 * - Adicionado editor de texto rico para descrição
 * - Adicionada seção de anexos com miniaturas
 */
import React, { useCallback } from 'react';
import { differenceInDays } from 'date-fns';
import { TrendingUp, Clock } from 'lucide-react';
import { CrmDeal } from '@/hooks/useCrmDeals';
import { DealMetricCard } from '../components/DealMetricCard';
import { DealActivityTimelinePremium } from '../components/DealActivityTimelinePremium';
import { DealInlineActivityCreator } from '../components/DealInlineActivityCreator';
import { DealDescriptionAndAttachments } from '@/components/crm/deals/DealDescriptionAndAttachments';
import { PriceDisplay } from '@/components/crm/PriceDisplay';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { sanitizeHtml } from '@/lib/sanitize';


// Props do componente
interface DealSummaryTabProps {
  deal: CrmDeal;
  onPhaseChange: (newPhaseId: string) => void;
  /** Conteúdo da sidebar injetado inline no mobile */
  sidebarContent?: React.ReactNode;
}

/**
 * Calcula dias na fase atual
 */
function getDaysInPhase(updatedAt: string): number {
  return differenceInDays(new Date(), new Date(updatedAt));
}

/**
 * Aba de resumo do negócio
 */
export function DealSummaryTab({ deal, onPhaseChange, sidebarContent }: DealSummaryTabProps) {
  const daysInPhase = getDaysInPhase(deal.updated_at);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Handler para salvar descrição HTML
  const handleSaveDescription = useCallback(async (html: string) => {
    try {
      // Sanitizar HTML antes de enviar
      const sanitized = sanitizeHtml(html);

      // Chamar RPC de atualização segura
      const { error } = await supabase.rpc('update_deal_description_html', {
        p_deal_id: deal.id,
        p_description_html: sanitized || null
      });

      if (error) {
        throw error;
      }

      // Invalidar cache para recarregar dados
      queryClient.invalidateQueries({ queryKey: ['crm-deal', deal.id] });
      queryClient.invalidateQueries({ queryKey: ['crm-deals'] });

      toast({
        title: 'Descrição salva',
        description: 'A descrição foi atualizada com sucesso',
      });
    } catch (error) {
      console.error('Erro ao salvar descrição:', error);
      toast({
        title: 'Erro ao salvar',
        description: 'Não foi possível salvar a descrição',
        variant: 'destructive',
      });
      throw error; // Re-lançar para o componente tratar
    }
  }, [deal.id, queryClient, toast]);

  return (
    <div className="space-y-4">
      {/* v2.1.0 (2026-05-16): badge de origem foi movido para a sidebar
          (seção "Informações" → linha "Cadastro") para evitar duplicação. */}

      {/* v2.2.0 (2026-09-03): o bloco de Workflow saiu daqui e passou a viver na
          aba dedicada "Fluxos de trabalho" do modal de negócio. */}

      {/* Sidebar inline no mobile — sem wrapper extra para evitar 3 níveis de cards aninhados */}
      {sidebarContent && (
        <div className="-mx-1">{sidebarContent}</div>
      )}

      {/* Grid de métricas principais */}
      <div className="grid grid-cols-3 gap-3">
        <DealMetricCard
          label="Valor"
          value={<PriceDisplay amount={deal.value} currency={deal.currency} size="sm" />}
          variant="primary"
        />
        <DealMetricCard
          icon={TrendingUp}
          label="Probabilidade"
          value={`${deal.probability ?? 0}%`}
          variant="default"
        />
        <DealMetricCard
          icon={Clock}
          label="Na fase"
          value={`${daysInPhase} dias`}
          variant={daysInPhase > 14 ? 'warning' : 'default'}
        />
      </div>

      {/* Descrição e Anexos unificados */}
      <DealDescriptionAndAttachments
        dealId={deal.id}
        content={deal.description_html}
        plainContent={deal.description}
        readOnly={deal.status !== 'open'}
        onSaveDescription={handleSaveDescription}
      />


      {/* Timeline sem card externo — mesmo padrão do modal de tarefas (mais espaço) */}

      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Atividades Recentes
          </h4>
        </div>
        <div className="pb-4 space-y-3">
          {/* Criador inline de atividades — UX equivalente ao modal de tarefas */}
          <DealInlineActivityCreator
            dealId={deal.id}
            canCreate={deal.status === 'open'}
          />
          <DealActivityTimelinePremium dealId={deal.id} isActive={true} paginated />
        </div>
      </div>
    </div>
  );
}
